/**
 * `components.MuiButton` — sizes, tonal/outlined/contained variants, print hiding.
 * Discoverability: `MuiButton`, `Button`, `variant`, `tonal`, `outlined`.
 *
 * @see https://mui.com/material-ui/api/button/
 *
 */
import { alpha } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';

import { buttonBorderRadius, buttonPadding, resolveButtonSize, type ButtonSize } from './buttonSizeTokens';
import { buttonFocusVisibleStyles } from './buttonFocusTokens';

function sizePaddingStyles(
  theme: Theme,
  size: ButtonSize,
): {
  filled: { padding: string };
  text: { padding: string };
} {
  return {
    filled: { padding: buttonPadding(theme, size, 'contained') },
    text: { padding: buttonPadding(theme, size, 'text') },
  };
}

export function muiButton(theme: Theme): Pick<Components<Theme>, 'MuiButton'> {
  const small = sizePaddingStyles(theme, 'small');
  const medium = sizePaddingStyles(theme, 'medium');
  const large = sizePaddingStyles(theme, 'large');

  return {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        size: 'medium',
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'add' },
          style: {},
        },
        {
          props: { variant: 'text' },
          style: {},
        },
        {
          props: { variant: 'contained' },
          style: {
            color: theme.palette.text.contrastText,
            '&.Mui-disabled': {
              // make disabled contained button faded main color instead of grey
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.contrastText,
            },
          },
        },
        // Size × variant padding (beats MUI built-in size paddings).
        // filled pad shared by contained / outlined / tonal / add (transparent border on non-outlined).
        { props: { size: 'small', variant: 'contained' }, style: small.filled },
        { props: { size: 'small', variant: 'outlined' }, style: small.filled },
        { props: { size: 'small', variant: 'text' }, style: small.text },
        { props: { size: 'small', variant: 'tonal' }, style: small.filled },
        { props: { size: 'small', variant: 'add' }, style: small.filled },
        { props: { size: 'medium', variant: 'contained' }, style: medium.filled },
        { props: { size: 'medium', variant: 'outlined' }, style: medium.filled },
        { props: { size: 'medium', variant: 'text' }, style: medium.text },
        { props: { size: 'medium', variant: 'tonal' }, style: medium.filled },
        { props: { size: 'medium', variant: 'add' }, style: medium.filled },
        { props: { size: 'large', variant: 'contained' }, style: large.filled },
        { props: { size: 'large', variant: 'outlined' }, style: large.filled },
        { props: { size: 'large', variant: 'text' }, style: large.text },
        { props: { size: 'large', variant: 'tonal' }, style: large.filled },
        { props: { size: 'large', variant: 'add' }, style: large.filled },
      ],
      styleOverrides: {
        root: ({ ownerState }) => {
          const size = resolveButtonSize(ownerState.size);
          const isOutlined = ownerState.variant === 'outlined';

          return {
            boxSizing: 'border-box',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: isOutlined ? undefined : 'transparent',
            borderRadius: buttonBorderRadius(theme, size),
            ...(size === 'large' ? theme.typography.bodyMedium : theme.typography.paragraphMedium),

            '&.Mui-disabled': {
              opacity: theme.palette.action.disabledOpacity,
            },
            '&.Mui-focusVisible': buttonFocusVisibleStyles(theme),
            ...(isOutlined &&
              ownerState.color === 'primary' && {
                color: theme.palette.text.primary,
                background: theme.palette.background.paper,
                borderColor: theme.palette.border.main,
                '&:hover': {
                  color: theme.palette.primary.main,
                  background: theme.palette.primary.light,
                  borderColor: theme.palette.border.dark,
                },
                '&.Mui-disabled': {
                  opacity: theme.palette.action.disabledOpacity,
                },
              }),
            ...(isOutlined &&
              ownerState.color && {
                background: theme.palette.background.paper,
                '&:hover': {
                  background:
                    theme.palette[
                      ownerState.color as 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
                    ]?.light || theme.palette.primary.light,
                },
              }),
            // Tonal - Primary (default)
            ...(ownerState.variant === 'tonal' &&
              (!ownerState.color || ownerState.color === 'primary') && {
                color: theme.palette.primary.main,
                background: theme.palette.primary.light,
                '&:hover': {
                  color: theme.palette.primary.dark,
                  background: theme.palette.secondary.light,
                },
                '&.Mui-disabled': {
                  opacity: theme.palette.action.disabledOpacity,
                },
              }),
            // Tonal - Secondary
            ...(ownerState.variant === 'tonal' &&
              ownerState.color === 'secondary' && {
                color: theme.palette.secondary.main,
                background: theme.palette.secondary.light,
                '&:hover': {
                  color: theme.palette.secondary.dark,
                  background: alpha(theme.palette.secondary.main, 0.4),
                },
                '&.Mui-disabled': {
                  opacity: theme.palette.action.disabledOpacity,
                },
              }),
            // Tonal - Error
            ...(ownerState.variant === 'tonal' &&
              ownerState.color === 'error' && {
                color: theme.palette.error.main,
                background: theme.palette.error.light,
                '&:hover': {
                  color: theme.palette.error.dark,
                  background: alpha(theme.palette.error.main, 0.4),
                },
                '&.Mui-disabled': {
                  opacity: theme.palette.action.disabledOpacity,
                },
              }),
            // Tonal - Warning
            ...(ownerState.variant === 'tonal' &&
              ownerState.color === 'warning' && {
                color: theme.palette.warning.main,
                background: theme.palette.warning.light,
                '&:hover': {
                  color: theme.palette.warning.dark,
                  background: alpha(theme.palette.warning.main, 0.4),
                },
                '&.Mui-disabled': {
                  opacity: theme.palette.action.disabledOpacity,
                },
              }),
            // Tonal - Info
            ...(ownerState.variant === 'tonal' &&
              ownerState.color === 'info' && {
                color: theme.palette.info.main,
                background: theme.palette.info.light,
                '&:hover': {
                  color: theme.palette.info.dark,
                  background: alpha(theme.palette.info.main, 0.4),
                },
                '&.Mui-disabled': {
                  opacity: theme.palette.action.disabledOpacity,
                },
              }),
            // Tonal - Success
            ...(ownerState.variant === 'tonal' &&
              ownerState.color === 'success' && {
                color: theme.palette.success.main,
                background: theme.palette.success.light,
                '&:hover': {
                  color: theme.palette.success.dark,
                  background: alpha(theme.palette.success.main, 0.4),
                },
                '&.Mui-disabled': {
                  opacity: theme.palette.action.disabledOpacity,
                },
              }),
            textTransform: 'none',
            textWrap: 'nowrap',
            '@media print': {
              display: 'none',
            },
          };
        },
      },
    },
  };
}
